"use client";

import { authUser } from "@/lib/actions/user.actions";
import { getUserRole } from "@/lib/utils";
import { User } from "@/types/index";
import { createContext, useContext, useEffect, useState } from "react";

type UserRole = string[];

type UserContextProps = {
  user: User | undefined;
  setUser: (user: User | undefined) => void;
  userRole: UserRole | undefined;
  setUserRole: (user: UserRole | undefined) => void;
  branchId: string;
  setBranchId: (branchId: string) => void;
};
const AuthContext = createContext<UserContextProps | undefined>(undefined);

export const AuthContextWrapper = ({
  children,
  initialBranchId,
}: {
  children: React.ReactNode;
  initialBranchId?: string;
}) => {
  const [user, setUser] = useState<User | undefined>(undefined);
  const [userRole, setUserRole] = useState<UserRole | undefined>(undefined);
  const [branchId, setBranchId] = useState<string>(initialBranchId || "");

  // Follow the centre chosen in the header. Without this the value stays at
  // whatever was selected on first load, so forms opened after a switch
  // defaulted to the previous centre.
  useEffect(() => {
    if (initialBranchId) setBranchId(initialBranchId);
  }, [initialBranchId]);

  useEffect(() => {
    const fetchUserRole = async () => {
      const user = await authUser();
      const userRole = getUserRole(user);
      setUserRole(userRole);
      setUser(user);
      // Set branch ID from user's role; if a saved branch ID exists,
      // verify it belongs to the current user, otherwise reset to user's first branch
      if (user?.branch_role?.[0]) {
        const userBranchIds = user.branch_role.map((br) => br.branch_id.toString());
        if (!initialBranchId || !userBranchIds.includes(initialBranchId)) {
          setBranchId(user.branch_role[0].branch_id.toString());
        }
      }
    };

    fetchUserRole();
  }, []);
  return (
    <AuthContext.Provider
      value={{ user, setUser, userRole, setUserRole, branchId, setBranchId }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuthContext = () => {
  const user = useContext(AuthContext);

  if (user == undefined) {
    throw new Error("User is undefined");
  }

  return user;
};
